import { Box, Skeleton, TableCell } from "@mui/material";
import TrialUsageWarning from "../../../common/components/TrialUsageWarning.tsx";
import { DotLegalEmptyState, DotLegalPaper, DotLegalTable, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import TrialExpiredRowWrapper from "../../../common/components/TrialExpiredRowWrapper.tsx";
import DotLegalLink from "../../../common/components/dotLegalLink/DotLegalLink.tsx";
import { LegalEntityAuditTabs } from "../../../legalEntity/LegalEntity.types.ts";
import SystemLinks from "../../../system/SystemLinks.tsx";
import ClassificationRisk from "../../../common/components/questionnaireComponents/classiificationRisk/ClassificationRisk.tsx";
import { LegalEntityScope } from "../../../legalEntity/legalEntities/LegalEntities.types.ts";
import QuestionnaireStatus from "../../../common/components/questionnaireComponents/questionnaireStatus/QuestionnaireStatus.tsx";
import QuestionnaireRisk from "../../../common/components/questionnaireComponents/questionnaireRisk/QuestionnaireRisk.tsx";
import QuestionnaireEvaluation from "../../../common/components/questionnaireComponents/questionnaireEvaluation/QuestionnaireEvaluation.tsx";
import LegalEntityAuditTableOverflowMenu from "../../../legalEntity/legalEntityAuditTab/legalEntityAuditTableOverflowMenu/LegalEntityAuditTableOverflowMenu.tsx";
import { LegalEntityAuditStatus } from "../../../legalEntity/legalEntityAuditTab/LegalEntityAudit.types.ts";
import ManagementReportButtons from "@common/components/managementReport/ManagementReportButtons";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import {
    LegalEntityAuditGroupAuditsTabRow,
    LegalEntityAuditGroupAuditTableModel,
    LegalEntityAuditGroupOverviewModel,
} from "../LegalEntityAuditGroupOverview.types.ts";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { isNullOrWhitespace } from "../../../common/stringOperations.ts";
import { useLegalEntityAuditGroupAuditsTab } from "./LegalEntityAuditGroupAuditsTab.hooks.ts";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import QuestionnaireReportOverflowButtons from "../../../common/components/questionnaireComponents/questionnaireReportButtons/QuestionnaireReportOverflowButtons.tsx";
import QuestionnaireReportButtons from "../../../common/components/questionnaireComponents/questionnaireReportButtons/QuestionnaireReportButtons.tsx";
import ExternalAuditEmptyStateIcon from "../../externalAuditsEmptyState.svg?react";
import DotLegalNoteDialog from "@common/components/dotlegalNote/dotLegalNoteDialog/DotLegalNoteDialog";
import TruncatedMentionText from "@common/components/mentionInput/TruncatedMentionText";
import { noteExcludedFromSearch } from "@common/tableSearch";
import { EntityType } from "../../../common/Common.types.ts";
import { isPreviewSupported } from "@common/components/documentPreview/DocumentPreview.hooks.ts";
import { useDocumentPreviewManager } from "@common/components/documentPreview/useDocumentPreviewManager.ts";
import ListPageToolbar from "@common/components/listPageToolbar/ListPageToolbar";
import FilterBar from "@common/components/filterBar/FilterBar";
import {
    allLegalEntityAuditGroupAuditColumns,
    defaultOrder,
    defaultOrderBy,
    LegalEntityAuditGroupAuditColumn,
} from "./LegalEntityAuditGroupAuditsTab.columns.ts";

export interface LegalEntityAuditGroupAuditsTabProps {
    auditGroupId: string;
    isLoading: boolean;
    auditsData: LegalEntityAuditGroupOverviewModel | undefined;
    refetchData: () => void;
}

function LegalEntityAuditGroupAuditsTab(props: LegalEntityAuditGroupAuditsTabProps) {
    const { vendorManagementFeatures } = usePlanContext();
    const { customerName } = useUserContext();
    const { translateString } = useTranslation();
    const { getLegalEntityUrl, getAuditGroupManagementReportUrl } = useUrlProvider();
    const {
        rows,
        tableHeaders,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        searchString,
        setSearchString,
        getReportPdfUrl,
        isDownloadingPdf,
        onDownloadReportAsPdf,
        auditInformationsIsLoading,
        onDownloadClick,
        getNoteMenuItem,
        noteDialogProps,
    } = useLegalEntityAuditGroupAuditsTab(props);

    const documents =
        props.auditsData?.audits
            ?.filter((x) => x.document)
            .map((x) => ({
                ...x.document!,
                entityId: x.legalEntityId,
                entityTitle: x.legalEntityName,
            })) ?? [];
    const { previewableDocuments, handleDocumentWithTypeAndIdClick } = useDocumentPreviewManager({
        documents: documents,
        entityType: EntityType.LegalEntity,
        entityId: "",
    });

    function getReportButtonMenuItems(row: LegalEntityAuditGroupAuditTableModel) {
        if (row.scoreEnabled) {
            return [
                {
                    menuItem: row.hasRecommendations ? translateString("downloadWithScoreAndRecommendations") : translateString("downloadWithScore"),
                    onClick: () => onDownloadReportAsPdf({ legalEntityAuditId: row.id, ...row }, false, row.hasRecommendations),
                },
                {
                    menuItem: row.hasRecommendations ? translateString("downloadWithoutScoreAndRecommendations") : translateString("downloadWithoutScore"),
                    onClick: () => onDownloadReportAsPdf({ legalEntityAuditId: row.id, ...row }, true, false),
                },
            ];
        } else {
            return [
                {
                    menuItem: translateString("downloadWithRecommendations"),
                    onClick: () => onDownloadReportAsPdf({ legalEntityAuditId: row.id, ...row }, false, true),
                },
                {
                    menuItem: translateString("downloadWithoutRecommendations"),
                    onClick: () => onDownloadReportAsPdf({ legalEntityAuditId: row.id, ...row }, false, false),
                },
            ];
        }
    }

    function getReportButtons(row: LegalEntityAuditGroupAuditTableModel) {
        const reportUrl = getReportPdfUrl(row.questionnaireResponseId, row.deadline, row.sender, row.legalEntityName, false, row.hasRecommendations);
        const showButtons = row.status !== LegalEntityAuditStatus.Opened && row.status !== LegalEntityAuditStatus.Sent && !row.closed;
        const downloadingPdf = isDownloadingPdf(row.id);

        if (!row.partOfCurrentPlan) {
            return undefined;
        }

        if (row.scoreEnabled || row.hasRecommendations) {
            return (
                <QuestionnaireReportOverflowButtons
                    reportPdfUrl={reportUrl}
                    reportPdfMenuItems={getReportButtonMenuItems(row)}
                    showButtons={showButtons}
                    isDownloadingPdf={downloadingPdf}
                />
            );
        }

        return (
            <QuestionnaireReportButtons
                isDownloadingPdf={downloadingPdf}
                onDownloadReportPdf={() => onDownloadReportAsPdf({ legalEntityAuditId: row.id, ...row }, true, row.hasRecommendations)}
                reportPdfUrl={reportUrl}
                showButtons={showButtons}
            />
        );
    }

    function getFilterBar() {
        return (
            <FilterBar
                isLoading={props.isLoading}
                filterData={getFilterOptions()}
                fetchFilterData={!props.isLoading && !!props.auditsData}
                onMenuClose={onFilterChange}
                onRemove={onFilterChange}
                pinnedFilterIds={pinnedFilterIds}
                onPinnedFilterIdsChange={onPinnedFilterIdsChange}
                searchValue={searchString}
                searchLabel={translateString("search")}
                onSearchChange={setSearchString}
            />
        );
    }

    function onStatusClick(row: LegalEntityAuditGroupAuditsTabRow) {
        if (isNullOrWhitespace(row.document!.link) && isPreviewSupported(row.document!.fileName!)) {
            handleDocumentWithTypeAndIdClick(row.document!, EntityType.LegalEntity, row.legalEntityId);
        } else {
            onDownloadClick(row.document!.documentId, row.document!.dataId!, row.document!.fileName ?? "", row.document!.link, row.legalEntityId);
        }
    }

    function renderCell(column: LegalEntityAuditGroupAuditColumn, row: LegalEntityAuditGroupAuditsTabRow) {
        switch (column) {
            case LegalEntityAuditGroupAuditColumn.LegalEntity:
                return (
                    <TableCell key={column}>
                        <Box component={"span"} sx={{ fontWeight: "bold" }}>
                            <DotLegalLink linkColor={"primary"} to={`${getLegalEntityUrl(row.legalEntityId, LegalEntityAuditTabs.Audits)}`}>
                                {row.legalEntityName}
                            </DotLegalLink>
                        </Box>
                    </TableCell>
                );
            case LegalEntityAuditGroupAuditColumn.UniqueIdentifier:
                return <TableCell key={column}>{row.uniqueIdentifier}</TableCell>;
            case LegalEntityAuditGroupAuditColumn.Type:
                return <TableCell key={column}>{row.legalEntityTypeString}</TableCell>;
            case LegalEntityAuditGroupAuditColumn.Contact:
                return (
                    <TableCell key={column}>
                        <DotLegalTooltip text={row.contactEmail}>
                            <Box>
                                {row.contact} {row.contactTypes.length > 0 && <>({row.contactTypesString})</>}
                            </Box>
                        </DotLegalTooltip>
                    </TableCell>
                );
            case LegalEntityAuditGroupAuditColumn.AdditionalRecipients:
                return <TableCell key={column}>{row.additionalRecipientNames}</TableCell>;
            case LegalEntityAuditGroupAuditColumn.Assets:
                return (
                    <TableCell key={column}>
                        <SystemLinks systems={row.assets} />
                    </TableCell>
                );
            case LegalEntityAuditGroupAuditColumn.Classifications:
                return (
                    <TableCell key={column} sx={{ minWidth: 350 }}>
                        {auditInformationsIsLoading ? (
                            <Skeleton width={"100%"} height={18} />
                        ) : (
                            row.classifications &&
                            row.classifications.length > 0 && (
                                <ClassificationRisk legalEntityId={row.id} legalEntityScope={LegalEntityScope.External} classifications={row.classifications} />
                            )
                        )}
                    </TableCell>
                );
            case LegalEntityAuditGroupAuditColumn.Status:
                return (
                    <TableCell key={column}>
                        <QuestionnaireStatus
                            showQuestionsStatistics={row.showQuestionsStatistics}
                            numberOfQuestionsAnswered={row.numberOfQuestionsAnswered}
                            totalNumberOfQuestions={row.totalNumberOfQuestions}
                            status={row.status}
                            lastestRecipientUpdate={row.lastestResponseChange}
                            onClick={row.status === LegalEntityAuditStatus.AuditOpinion && row.document ? () => onStatusClick(row) : undefined}
                        />
                    </TableCell>
                );
            case LegalEntityAuditGroupAuditColumn.RiskLevel:
                return (
                    <TableCell key={column}>
                        <QuestionnaireRisk riskLevel={row.riskLevel} riskName={row.riskName} scoreEnabled={row.scoreEnabled} status={row.status} />
                    </TableCell>
                );
            case LegalEntityAuditGroupAuditColumn.Score:
                return (
                    <TableCell key={column} align="right">
                        {row.scoreString}
                    </TableCell>
                );
            case LegalEntityAuditGroupAuditColumn.Evaluation:
                return (
                    <TableCell key={column}>
                        <QuestionnaireEvaluation evaluation={row.evaluation} evaluationStatus={row.evaluationStatus} />
                    </TableCell>
                );
            case LegalEntityAuditGroupAuditColumn.AuditReport:
                return <TableCell key={column}>{getReportButtons(row)}</TableCell>;
            case LegalEntityAuditGroupAuditColumn.ReminderSent:
                return <TableCell key={column}>{row.remindersPaused ? translateString("remindersPaused") : row.reminderSentString}</TableCell>;
            case LegalEntityAuditGroupAuditColumn.Note:
                return (
                    <TableCell key={column}>
                        <TruncatedMentionText value={row.note} />
                    </TableCell>
                );
        }
    }

    return (
        <DotLegalPaper>
            {!vendorManagementFeatures.auditGroups && (
                <TrialUsageWarning
                    headerKey={translateString("auditGroupsLockedWarningHeader")}
                    textKey={translateString("auditGroupsLockedWarning")}
                    marginBottom={2}
                />
            )}
            {props.isLoading || (props.auditsData && props.auditsData.audits.length > 0) ? (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <ListPageToolbar leftContent={getFilterBar()} />
                    <DotLegalTable
                        headers={tableHeaders}
                        rowsPerPage={30}
                        searchString={searchString}
                        columnsExcludedFromFilter={noteExcludedFromSearch}
                        defaultOrderBy={defaultOrderBy}
                        defaultOrder={defaultOrder}
                        isLoading={props.isLoading}
                        paginationLabelOf={translateString("labelOf")}
                        data={rows}
                        clickableRows={false}
                        emptyText={translateString("noData")}
                        hasOverflowMenu
                        renderRow={(row, i) => {
                            return (
                                <TrialExpiredRowWrapper
                                    key={row.id + i}
                                    tooltip={""}
                                    onNonExpiredClick={() => {}}
                                    expired={!row.partOfCurrentPlan}
                                    planUpgradeText={translateString("auditGroupLocked")}
                                >
                                    {allLegalEntityAuditGroupAuditColumns.map((column) => renderCell(column, row))}

                                    <TableCell
                                        align={"right"}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                    >
                                        {!props.isLoading && props.auditsData && (
                                            <LegalEntityAuditTableOverflowMenu
                                                key={row.id}
                                                auditId={row.id}
                                                isClosed={row.closed}
                                                status={row.status}
                                                canBeDeleted={row.closed || row.status === LegalEntityAuditStatus.Submitted}
                                                questionnaireName={row.questionnaireName}
                                                deadline={row.deadline}
                                                auditNote={row.note}
                                                responsibleId={props.auditsData.responsibleId}
                                                contactId={row.contactId}
                                                contactName={row.contact}
                                                additionalRecipientIds={row.additionalRecipients.map((additionalRecipient) => additionalRecipient.id)}
                                                legalEntityId={row.legalEntityId}
                                                responseExternalId={row.responseExternalId}
                                                evaluation={row.evaluation}
                                                evaluationStatus={row.evaluationStatus}
                                                onAuditClosed={props.refetchData}
                                                onAuditReopened={props.refetchData}
                                                onAuditDeleted={props.refetchData}
                                                onResponsibleChanged={props.refetchData}
                                                onContactChanged={props.refetchData}
                                                onReminderSent={props.refetchData}
                                                onAuditEvaluated={props.refetchData}
                                                disableChangeResponsible
                                                onRemindersToggled={props.refetchData}
                                                remindersPaused={row.remindersPaused}
                                                questionnaireResponseId={row.questionnaireResponseId}
                                                questionnaireId={row.questionnaireId}
                                                legalEntityName={row.legalEntityName}
                                                partOfCurrentPlan={row.partOfCurrentPlan}
                                                createdAsAuditorReport={row.createdAsAuditorReport}
                                                additionalMenuItems={[getNoteMenuItem(row)]}
                                            />
                                        )}
                                    </TableCell>
                                </TrialExpiredRowWrapper>
                            );
                        }}
                    />
                    {props.auditsData && props.auditsData.sent && (
                        <ManagementReportButtons
                            reportUrl={getAuditGroupManagementReportUrl(props.auditsData.id)}
                            groupName={props.auditsData.name}
                            sender={props.auditsData.sender ?? customerName}
                            enabled={vendorManagementFeatures.auditGroups}
                            lockedText={translateString("auditGroupLocked")}
                        />
                    )}
                </Box>
            ) : (
                <DotLegalEmptyState icon={<ExternalAuditEmptyStateIcon />} text={"Du har ingen udsendte audits på denne auditgruppe"} />
            )}

            {noteDialogProps && <DotLegalNoteDialog {...noteDialogProps} />}
        </DotLegalPaper>
    );
}

export default LegalEntityAuditGroupAuditsTab;
